import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface MachineType {
  id: number;
  name: string;
}

export interface MotorArrangement {
  id: number;
  modelName: string;
  blobStorageReference?: string;
  modelProductionYear:string;
  supplier: string;
  frameSize: string;
  supplierReference: string;
  isOldArrangement: boolean;
}

export interface MatchedAlternative {
  id: number;
  alternative: string;
}
export interface MotorArrangementPart {
  legend: string;
  partNumber: string;
  partNumberQuantity: number;
  partDescription: string;
  flag: boolean;
  motorInvestigationPartMasterId: number;
}

export interface InvestigationReport {
  id: number;
  motorInvestigationFlowId: number;
  motorInvestigationFlowAlternativeId: number;
  machineTypeId: number;
  machineTypeName: string;
  oldMotorArrangementId: number;
  oldMotorArrangementBlobStorageReference: string;
  newMotorArrangementId: number;
  newMotorArrangementBlobStorageReference: string;
  fieldInstruction: string;
  internalNote: string;
  oldMotorArrangementParts: MotorArrangementPart[];
  newMotorArrangementParts: MotorArrangementPart[];
  reportDocuments: string[];
}


const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

const baseQuery = createBackendBaseQuery(BASE_URL);

export const comparisonReportDataApiSlice = createApi({
  reducerPath: 'comparisonReportDataApi',
  baseQuery: baseQuery,
  tagTypes: ['MachineTypeMasterData', 'MotorArrangementMasterData', 'MatchedAlternativeData', 'MotorArrangementImage','Tickets','MotorInvestigationReportData'],
  endpoints(builders) {
    return {
      fetchMachineTypeMasterData: builders.query<MachineType[], void>({
        query: () => ({
          url: API_URLS.MOTOR_SERVICE.MACHINE_TYPE,
          method: 'GET',
        }),
        providesTags: ['MachineTypeMasterData'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorArrangementData: builders.query<MotorArrangement[], void>({
        query: () => ({
          url: API_URLS.MOTOR_SERVICE.GET_MOTOR_ARRANGEMENT,
          method: 'GET',
        }),
        providesTags: ['MotorArrangementMasterData'],
        transformErrorResponse: transformApiError,
      }),
      fetchMatchedAlternativeData: builders.query<MatchedAlternative[], number>({
        query: (id) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.FETCH_MATCHED_ALTERNATIVE}?motorInvestigationFlowId=${id}`,
          method: 'GET',
        }),
        providesTags: ['MatchedAlternativeData'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorInvestigationReport: builders.query<InvestigationReport[], number>({
        query: (id) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.FETCH_REPORT}?motorInvestigationFlowId=${id}`,
          method: 'GET',
        }),
        providesTags: ['MotorInvestigationReportData'],
        transformErrorResponse: transformApiError,
      }),
      saveMotorInvestigationReport: builders.mutation<number, { InvestigationReportData: InvestigationReport[] }>({
        query({ InvestigationReportData }) {
          return {
            url: API_URLS.MOTOR_INVESTIGATION.SAVE_REPORT,
            method: 'POST',
            body: InvestigationReportData,
          };
        },
        invalidatesTags: [],
        transformErrorResponse: transformApiError,
      }),
      getMotorArrangementImage: builders.query<string, string>({
        query: (motorImageBlobStorageReference) => ({
          url: `${API_URLS.MOTOR_SERVICE.GET_MOTOR_ARRANGEMENT_IMAGE}?motorImageBlobStorageReference=${motorImageBlobStorageReference}`,
          method: 'GET',
        }),
        providesTags: ['MotorArrangementImage'],
        transformErrorResponse: transformApiError,
      }), 
      saveMotorArrangementData: builders.mutation<number, { MotorArrangementData: MotorArrangement, ArrangementImage: File }>({
        query({ MotorArrangementData, ArrangementImage }) {
          const formData = new FormData();
           formData.append('ModelName', MotorArrangementData.modelName.trim());
          formData.append('ModelProductionYear', MotorArrangementData.modelProductionYear.trim());
          formData.append('Supplier', MotorArrangementData.supplier.trim());
          formData.append('FrameSize', MotorArrangementData.frameSize.trim());
          formData.append('SupplierReference', MotorArrangementData.supplierReference.trim());
          formData.append('IsOldArrangement', MotorArrangementData.isOldArrangement.toString());
          // Add file if it exists
          if (ArrangementImage) {
            formData.append('file', ArrangementImage);
          }

          return {
            url: API_URLS.MOTOR_SERVICE.SAVE_MOTOR_ARRANGEMENT,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: ['MotorArrangementImage'],
        transformErrorResponse: transformApiError,
      }), 
      SendReportForApproval: builders.mutation<number, { motorInvestigationTicketId: number, motorInvestigationFlowId: number }>({
        query({ motorInvestigationTicketId, motorInvestigationFlowId }) {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.SEND_REPORT_FOR_APPROVAL}?motorInvestigationTicketId=${motorInvestigationTicketId}&motorInvestigationFlowId=${motorInvestigationFlowId}`,
            method: 'POST'
          };
        },
        invalidatesTags: ['Tickets'],
        transformErrorResponse: transformApiError,
      }),
      UploadReportDocuments: builders.mutation<string[], { reportDocuments: File[]}>({
        query({ reportDocuments }) {
          const formData = new FormData();
          reportDocuments.forEach((file) => {
            formData.append('reportDocuments', file);
          });
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.UPLOAD_REPORT_DOCUMENTS}`,
            method: 'POST',
            body: formData,
          };
        },
       invalidatesTags: [],
       transformErrorResponse: transformApiError,   
      }),
      deleteReportDocuments: builders.mutation<void, { documents: string[]}>({
        query({ documents }) {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.DELETE_REPORT_DOCUMENTS}`,
            method: 'POST',
            body: documents,
          };
        },
       invalidatesTags: [],
       transformErrorResponse: transformApiError,
      }),
      downloadReportDocuments: builders.mutation<Blob, { document: string}>({
        query({ document }) {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION.DOWNLOAD_REPORT_DOCUMENTS}?documentName=${document}`,
            method: 'POST',
            responseHandler: async (response) => response.blob(),
          };
        },
       transformResponse: (response: Blob) => response,
       invalidatesTags: [],
       transformErrorResponse: transformApiError,
      }),   
    };
  },
});

export const { useFetchMachineTypeMasterDataQuery, useLazyFetchMachineTypeMasterDataQuery,
  useFetchMotorArrangementDataQuery, useLazyFetchMotorArrangementDataQuery,
  useFetchMatchedAlternativeDataQuery, useLazyFetchMatchedAlternativeDataQuery,
  useSaveMotorInvestigationReportMutation, 
  useGetMotorArrangementImageQuery, useLazyGetMotorArrangementImageQuery,
  useSaveMotorArrangementDataMutation, useSendReportForApprovalMutation,
  useFetchMotorInvestigationReportQuery, useLazyFetchMotorInvestigationReportQuery,
  useUploadReportDocumentsMutation,
  useDeleteReportDocumentsMutation,
  useDownloadReportDocumentsMutation
} = comparisonReportDataApiSlice;
